import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createStepUploadLink, listStepImages } from '../services/api';
import AuthImage from './AuthImage';
import QrCode from './QrCode';

const POLL_INTERVAL_MS = 3000;

function formatCountdown(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * "Photograph it with your phone" — shown on the desktop, scanned by the phone.
 *
 * While it is open the step's photos are polled, so a shot taken outside
 * appears here within a few seconds without anyone reloading anything. Polling
 * rather than a socket: no server state, and a failed tick simply costs one
 * refresh instead of breaking the window.
 */
export default function MobileUploadModal({
  reportId,
  stepId,
  knownImageIds = [],
  onClose,
  onReceived,
}) {
  const { t } = useTranslation();
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [received, setReceived] = useState([]);
  const [expiresAt, setExpiresAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);

  // Seeded with what the wizard already has on screen, so a photo landing
  // between opening the window and the first poll still counts as new.
  const seen = useRef(new Set(knownImageIds));

  useEffect(() => {
    let cancelled = false;
    createStepUploadLink(reportId, stepId)
      .then((data) => {
        if (cancelled) return;
        setLink(data);
        setExpiresAt(Date.now() + data.expires_in * 1000);
      })
      .catch(() => {
        if (!cancelled) setError(t('upload.linkFailed'));
      });
    return () => { cancelled = true; };
  }, [reportId, stepId, t]);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      try {
        const images = await listStepImages(reportId, stepId);
        if (stopped) return;
        const fresh = images.filter((img) => !seen.current.has(img.id));
        if (fresh.length) {
          fresh.forEach((img) => seen.current.add(img.id));
          setReceived((prev) => [...fresh, ...prev]);
          onReceived?.(fresh);
        }
      } catch {
        // A missed tick is harmless — the next one catches up.
      }
    };
    poll();
    const timer = setInterval(poll, POLL_INTERVAL_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [reportId, stepId, onReceived]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copyLink = useCallback(async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard refused (insecure origin, permission) — the QR still works.
    }
  }, [link]);

  const secondsLeft = expiresAt ? Math.max(0, Math.round((expiresAt - now) / 1000)) : null;
  const expired = secondsLeft === 0;

  return (
    <div className="qr-backdrop" onClick={onClose} role="presentation">
      <div
        className="qr-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('upload.fromPhone')}
      >
        <div className="qr-modal-header">
          <h3>{t('upload.fromPhone')}</h3>
          <button className="qr-modal-close" onClick={onClose} aria-label={t('wizard.cancel')}>
            ×
          </button>
        </div>

        {error && <p className="qr-error">{error}</p>}

        {!error && !link && <p className="qr-muted">{t('auth.loading')}</p>}

        {link && (
          <>
            <div className={`qr-frame ${expired ? 'is-expired' : ''}`}>
              <QrCode value={link.url} title={t('upload.qrAlt')} />
              {expired && <div className="qr-expired-veil">{t('upload.linkExpired')}</div>}
            </div>

            <ol className="qr-steps">
              <li>{t('upload.stepScan')}</li>
              <li>{t('upload.stepShoot')}</li>
              <li>{t('upload.stepAppear')}</li>
            </ol>

            <p className="qr-muted">
              {link.step_title
                ? t('upload.targetStep', { step: link.step_title })
                : t('upload.targetReport', { report: link.report_title })}
              {secondsLeft !== null && !expired && (
                <> · {t('upload.validFor', { time: formatCountdown(secondsLeft) })}</>
              )}
            </p>

            <button className="btn btn-secondary btn-sm" onClick={copyLink}>
              {copied ? t('upload.linkCopied') : t('upload.copyLink')}
            </button>

            <div className="qr-live">
              {received.length === 0 ? (
                <>
                  <span className="qr-live-dot" />
                  {t('upload.waitingForPhotos')}
                </>
              ) : (
                t('upload.photosReceived', { n: received.length })
              )}
            </div>

            {received.length > 0 && (
              <div className="qr-thumbs">
                {received.map((img) => (
                  <AuthImage
                    key={img.id}
                    reportId={reportId}
                    filename={img.annotated_filename || img.cropped_filename || img.filename}
                    alt={img.original_filename}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
